import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../components/Topbar";
import useLang from "../hooks/useLang";
import api from "../api";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import CloneAvatar from "../components/CloneAvatar";
import { Plus, ArrowUpRight, User, Users, Globe, Briefcase, Leaf, Link as LinkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

function VisibilityBadge({ v }) {
  const map = {
    private: { icon: <User size={11} weight="bold" />, label: "PRIVATE" },
    specific: { icon: <Users size={11} weight="bold" />, label: "SHARED" },
    public: { icon: <Globe size={11} weight="bold" />, label: "PUBLIC" },
  };
  const m = map[v] || map.private;
  return (
    <span className="tag inline-flex items-center gap-1.5">
      {m.icon} {m.label}
    </span>
  );
}

function CloneCard({ clone, onOpen }) {
  return (
    <button
      onClick={onOpen}
      data-testid={`clone-card-${clone.id}`}
      className="group text-left border border-zinc-200 rounded-md p-6 bg-white hover:border-zinc-950 transition-all"
    >
      <div className="flex items-start justify-between mb-6">
        <CloneAvatar clone={clone} size={48} />
        <ArrowUpRight size={18} className="text-zinc-400 group-hover:text-zinc-950 transition-colors" />
      </div>
      <div className="overline mb-2 flex items-center gap-2">
        /// {clone.industry}
        {clone.linked_clone_id && <LinkIcon size={11} weight="bold" className="text-zinc-950" />}
      </div>
      <h3 className="font-heading text-xl tracking-tight mb-1.5 font-medium">{clone.role_name}</h3>
      <p className="text-sm text-zinc-500 line-clamp-2 mb-5 leading-relaxed">{clone.responsibilities}</p>
      <div className="flex items-center justify-between pt-4 border-t border-zinc-100">
        <span className="text-xs font-mono text-zinc-500">
          {clone.answer_count || 0} answers
        </span>
        <VisibilityBadge v={clone.visibility} />
      </div>
    </button>
  );
}

export default function Dashboard() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [myClones, setMyClones] = useState([]);
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState(() => localStorage.getItem("dashboard_section") || "work");

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/clones"), api.get("/clones/accessible")]);
      setMyClones(a.data);
      setShared(b.data);
    } catch (e) {
      toast.error(t("errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const changeSection = (v) => {
    setSection(v);
    localStorage.setItem("dashboard_section", v);
  };

  const workClones = useMemo(() => myClones.filter((c) => c.category !== "personal"), [myClones]);
  const personalClones = useMemo(() => myClones.filter((c) => c.category === "personal"), [myClones]);
  const currentList = section === "personal" ? personalClones : workClones;
  const currentShared = useMemo(() => shared.filter((c) => (section === "personal" ? c.category === "personal" : c.category !== "personal")), [shared, section]);

  return (
    <div className="min-h-screen bg-white">
      <Topbar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-10 lg:py-14">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="overline mb-3">/// Dashboard</div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-light">{t("myClones")}</h1>
          </div>
          <Button
            onClick={() => navigate(`/new?category=${section}`)}
            data-testid="create-clone-btn"
            className="h-11 bg-zinc-950 hover:bg-zinc-800 rounded-md font-medium gap-2"
          >
            <Plus size={16} weight="bold" /> {t("createClone")}
          </Button>
        </div>

        <Tabs value={section} onValueChange={changeSection} className="mb-8">
          <TabsList className="bg-transparent border-b border-zinc-200 rounded-none w-full justify-start gap-1 p-0 h-auto">
            <TabsTrigger
              value="work"
              data-testid="section-work-tab"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-950 data-[state=active]:bg-transparent px-5 py-3 text-sm font-mono uppercase tracking-wider data-[state=active]:shadow-none gap-2"
            >
              <Briefcase size={14} weight="duotone" /> {t("workClones")}
              <span className="tag ml-2">{workClones.length}</span>
            </TabsTrigger>
            <TabsTrigger
              value="personal"
              data-testid="section-personal-tab"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-950 data-[state=active]:bg-transparent px-5 py-3 text-sm font-mono uppercase tracking-wider data-[state=active]:shadow-none gap-2"
            >
              <Leaf size={14} weight="duotone" /> {t("personalClones")}
              <span className="tag ml-2">{personalClones.length}</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="text-zinc-500 text-sm font-mono">{t("loading")}</div>
        ) : currentList.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-md p-16 text-center">
            <p className="text-zinc-500 text-sm mb-6">{t("noClonesYet")}</p>
            <Button onClick={() => navigate(`/new?category=${section}`)} data-testid="empty-create-btn" className="bg-zinc-950 hover:bg-zinc-800 rounded-md gap-2">
              <Plus size={16} weight="bold" /> {t("createClone")}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="my-clones-grid">
            {currentList.map((c) => (
              <CloneCard key={c.id} clone={c} onOpen={() => navigate(`/clone/${c.id}`)} />
            ))}
          </div>
        )}

        <div className="mt-16">
          <div className="overline mb-6">/// {t("sharedWithMe")}</div>
          {currentShared.length === 0 ? (
            <p className="text-sm text-zinc-500">{t("noSharedClones")}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shared-clones-grid">
              {currentShared.map((c) => (
                <CloneCard key={c.id} clone={c} onOpen={() => navigate(`/clone/${c.id}`)} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
